import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Org } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useAuth, useOrgOptional } from '../../lib/auth.js';
import { Button, Card, Field, Input, useToast } from '../../components/ui.js';
import { ZestMark } from '../../components/ZestMark.js';
import './auth.css';

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

export function CreateOrgPage() {
  const { me, refresh } = useAuth();
  const { setOrgId, orgs } = useOrgOptional();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  // Auto-derive the slug from the name until the user edits it themselves.
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const org = await apiFetch<Org>('/orgs', {
        method: 'POST',
        body: { name: name.trim(), ...(slug.trim() ? { slug: slug.trim() } : {}) },
      });
      await refresh();
      setOrgId(org.id);
      toast(`${org.name} created — you are its admin.`, 'success');
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not create the organisation.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-viewport">
      <Card className="auth-card auth-card-wide">
        <div className="auth-brand">
          <ZestMark className="auth-logo" />
          <h1>Create your organisation</h1>
        </div>
        <p className="auth-tagline">
          As the first user you become the organisation admin: you control members, roles,
          billing and compliance workspaces (a starter shared corpus is set up for you).
        </p>

        {me && me.pendingInvitations.length > 0 && (
          <div className="auth-error" role="note" style={{ background: 'var(--color-info-soft)', color: 'var(--color-info)' }}>
            You have {me.pendingInvitations.length} pending invitation(s) to join an existing
            organisation. If you were invited to join a team, do not fill in this form —{' '}
            <Link to="/invitations/accept">accept your invitation</Link> instead.
          </div>
        )}

        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={(e) => void submit(e)}>
          <Field label="Organisation name" required>
            {(props) => (
              <Input
                {...props}
                required
                minLength={2}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugTouched) setSlug(slugify(e.target.value));
                }}
                placeholder="Juicy Media Ltd"
              />
            )}
          </Field>
          <Field
            label="Slug"
            hint="Short identifier used in URLs — created from the name, edit to override"
          >
            {(props) => (
              <Input
                {...props}
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value.toLowerCase());
                }}
                pattern="[a-z0-9][a-z0-9-]*"
                placeholder="juicy-media"
              />
            )}
          </Field>
          <div className="auth-actions">
            <Button type="submit" loading={busy}>
              Create organisation
            </Button>
            {orgs.length > 0 && <Link to="/">Back to your workspace</Link>}
          </div>
        </form>
      </Card>
    </div>
  );
}
